/**
 * CABErrorState — Error display with resume option.
 */

import React from 'react';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import type { CABErrorInfo } from '../../types/cab';

interface CABErrorStateProps {
  error: CABErrorInfo;
  onResume?: () => void;
  onRegenerate?: () => void;
}

export const CABErrorState: React.FC<CABErrorStateProps> = ({
  error,
  onResume,
  onRegenerate,
}) => {
  return (
    <div className="flex flex-col items-center justify-center p-bg-v2-3xl text-center h-full">
      <div className="w-16 h-16 rounded-full bg-bg-error-primary flex items-center justify-center mb-bg-v2-xl">
        <Icon name={IconName.Alert} size="xl" className="!text-text-error-primary" inheritColor />
      </div>

      <Text variant="text-md" weight="semibold" className="text-text-primary mb-bg-v2-sm">
        Generation Failed
      </Text>

      <Text variant="text-sm" className="text-text-secondary max-w-md leading-relaxed mb-bg-v2-xs">
        {error.message}
      </Text>

      {error.phase && (
        <Text variant="text-xs" className="text-text-tertiary mb-bg-v2-xl">
          Failed during: {error.phase}{error.step ? ` / ${error.step}` : ''}
        </Text>
      )}

      <div className="flex gap-bg-v2-sm">
        {error.is_resumable && onResume && (
          <button
            type="button"
            onClick={onResume}
            className="
              bg-bg-brand-solid text-text-white
              px-bg-v2-lg py-bg-v2-md rounded-bg-v2-md
              font-bg-v2-semibold text-bg-v2-sm
              transition-colors container-shadow-sm
              hover:bg-bg-brand-solid-hover
            "
          >
            Resume from Checkpoint
          </button>
        )}
        {onRegenerate && (
          <button
            type="button"
            onClick={onRegenerate}
            className="
              bg-bg-primary text-text-secondary
              px-bg-v2-lg py-bg-v2-md rounded-bg-v2-md
              border border-border-primary
              font-bg-v2-medium text-bg-v2-sm
              transition-colors hover:bg-bg-primary-hover
            "
          >
            Regenerate from Scratch
          </button>
        )}
      </div>
    </div>
  );
};
